import { useEffect, useState } from 'react';
import { Button } from '@paulownia/shared/components/ui/button';
import { Card } from '@paulownia/shared/components/ui/card';
import { MapPin, Clock, ArrowLeft, Phone, Package, Store } from 'lucide-react';
import { getOptionLabels, formatQuantityDisplay, safeToFixed } from '@paulownia/shared/types';
import { getMarketById, type Market } from '@paulownia/shared';
import type { OrderTrackingProps } from './types';
import { CrossSellCTA } from './CrossSellCTA';

export function OrderTracking({ orderId, customerName, pickupTime, tenantName, tenantPhoneNumber, marketId, cart, products, onBackClick }: OrderTrackingProps) {
    const displayTime = pickupTime.replace(':', 'h');
    const [market, setMarket] = useState<Market | null>(null);

    useEffect(() => {
        if (!marketId) return;
        getMarketById(marketId).then(setMarket);
    }, [marketId]);

    // Calculate total
    const total = cart.reduce((sum, item) => {
        const product = products.find(p => p.id === item.productId);
        if (!product) return sum;
        const itemPrice = item.price ?? product.price ?? 0;
        return sum + itemPrice * item.quantity;
    }, 0);

    return (
        <div className="h-screen flex flex-col bg-gray-50">
            {/* Fixed header with back button */}
            <div className="flex-shrink-0 bg-gray-50 border-b border-gray-200">
                <div className="max-w-md mx-auto px-4 py-3 flex items-center">
                    <button
                        onClick={onBackClick}
                        className="p-2 -ml-2 rounded-full hover:bg-gray-200 active:bg-gray-300 transition-colors"
                        aria-label="Retour"
                    >
                        <ArrowLeft className="w-5 h-5 text-gray-700" />
                    </button>
                    <span className="ml-2 font-medium text-gray-900">Confirmation</span>
                </div>
            </div>

            {/* Scrollable content area */}
            <div className="flex-1 overflow-y-auto">
            <div className="p-4 pb-8 flex justify-center">
            <Card className="w-full max-w-md p-8 shadow-xl">
                {/* Header */}
                <div className="text-center mb-4">
                    <h1 className="text-2xl font-bold text-gray-900 mb-1">
                        Commande enregistree
                    </h1>
                    <p className="text-gray-600">
                        Merci {customerName} !
                    </p>
                    <p className="text-sm text-gray-500 mt-1">
                        Votre commande a bien ete prise en compte.
                    </p>
                </div>

                {/* Order details - compact grouped layout */}
                <div className="bg-gray-50 rounded-lg p-3 mb-4">
                    {/* Market pickup location */}
                    {market && (
                        <div className="flex items-center gap-3 py-2">
                            <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                                <Store className="w-4 h-4 text-orange-600" />
                            </div>
                            <div className="flex-1">
                                <p className="text-xs text-gray-500">Lieu de retrait</p>
                                <p className="font-medium text-gray-900 text-sm">Marché de {market.city}</p>
                            </div>
                        </div>
                    )}

                    {/* Merchant */}
                    <div className="flex items-center gap-3 py-2 border-t border-gray-200">
                        <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                            <MapPin className="w-4 h-4 text-orange-600" />
                        </div>
                        <div className="flex-1">
                            <p className="text-xs text-gray-500">Retrait chez</p>
                            <p className="font-medium text-gray-900 text-sm">{tenantName}</p>
                        </div>
                    </div>

                    {/* Pickup time */}
                    <div className="flex items-center gap-3 py-2 border-t border-gray-200">
                        <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                            <Clock className="w-4 h-4 text-orange-600" />
                        </div>
                        <div className="flex-1">
                            <p className="text-xs text-gray-500">Heure de retrait</p>
                            <p className="font-medium text-gray-900 text-sm">{displayTime}</p>
                        </div>
                    </div>
                </div>

                {/* Order Lines */}
                {cart.length > 0 && (
                    <div className="border-t pt-4 mb-6">
                        <h3 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                            <Package className="w-4 h-4" />
                            Detail de la commande
                        </h3>
                        <div className="space-y-2">
                            {cart.map((item, index) => {
                                const product = products.find(p => p.id === item.productId);
                                const optionLabels = product ? getOptionLabels({
                                    productId: item.productId,
                                    quantity: item.quantity,
                                    options: item.options,
                                    portionSizeId: item.portionSizeId,
                                    preparedQuantity: 0,
                                }, product) : [];

                                return (
                                    <div
                                        key={index}
                                        className="flex items-start justify-between py-2 border-b border-gray-100 last:border-0"
                                    >
                                        <div className="flex-1">
                                            <p className="font-medium text-gray-900">
                                                {item.productName}
                                            </p>
                                            {optionLabels.length > 0 && (
                                                <p className="text-sm text-gray-500">
                                                    {optionLabels.join(', ')}
                                                </p>
                                            )}
                                        </div>
                                        <div className="text-right">
                                            <span className="font-medium text-gray-900">
                                                {formatQuantityDisplay(item.quantity, product, item.portionSizeId, {
                                                    productId: item.productId,
                                                    quantity: item.quantity,
                                                    options: item.options,
                                                    portionSizeId: item.portionSizeId,
                                                    preparedQuantity: 0,
                                                    servings: item.servings,
                                                })}
                                            </span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Total */}
                        {total > 0 && (
                            <div className="flex items-center justify-between pt-3 mt-3 border-t border-gray-200">
                                <span className="font-medium text-gray-700">Total</span>
                                <span className="text-lg font-bold text-green-700">{safeToFixed(total, 2)} EUR</span>
                            </div>
                        )}
                    </div>
                )}

                {/* Merchant contact */}
                {tenantPhoneNumber && (
                    <div className="mb-6">
                        <a
                            href={`tel:${tenantPhoneNumber}`}
                            className="flex items-center justify-center gap-2 text-sm text-gray-600 hover:text-orange-600 transition-colors"
                        >
                            <Phone className="w-4 h-4" />
                            <span>Une question ? Appelez le {tenantPhoneNumber}</span>
                        </a>
                    </div>
                )}

                {/* Cross-sell CTA - Discover other merchants at this market */}
                <CrossSellCTA marketId={marketId} />

                {/* Back button - single CTA */}
                <Button
                    size="lg"
                    className="w-full text-lg h-14 bg-orange-600 hover:bg-orange-700"
                    onClick={onBackClick}
                >
                    Retour a l'accueil
                </Button>

                {/* Order ID for reference */}
                <p className="text-xs text-gray-400 text-center mt-4">
                    Ref: {orderId.slice(0, 8).toUpperCase()}
                </p>
            </Card>
            </div>
            </div>
        </div>
    );
}
